import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { 
  Settings, 
  Save, 
  RefreshCw, 
  Shield, 
  Clock, 
  FileText, 
  AlertTriangle, 
  CheckCircle,
  Database,
  Cloud,
  Zap,
  Bell,
  Mail,
  Smartphone,
  Globe,
  Lock,
  Eye,
  Users,
  Building
} from "lucide-react";
import AIConfigurationService from "@/api/aiConfiguration.service";
import DocumentTypesService from "@/api/documentTypes.service";

interface ProcessingSettings {
  // General Settings
  enableAutoProcessing: boolean;
  maxConcurrentProcessing: number;
  processingTimeout: number;
  retryAttempts: number;
  retryDelay: number;
  
  // Quality Settings
  confidenceThreshold: number;
  manualReviewThreshold: number;
  enableQualityChecks: boolean;
  
  // Storage Settings
  storageProvider: 'local' | 'aws_s3' | 'google_cloud' | 'azure_blob';
  retentionPeriod: number;
  enableCompression: boolean;
  enableEncryption: boolean;
  
  // Notification Settings
  enableEmailNotifications: boolean;
  enableSmsNotifications: boolean;
  enableWebhookNotifications: boolean;
  notificationEmail: string;
  webhookUrl: string;
  
  // Security Settings
  enableAuditLogging: boolean;
  enableAccessControl: boolean;
  sessionTimeout: number;
  enableTwoFactor: boolean;
  
  // Performance Settings
  enableCaching: boolean;
  cacheExpiration: number;
  enableLoadBalancing: boolean;
  maxFileSize: number;
  allowedFileTypes: string[];
  
  // Business Rules
  enableWorkflowAutomation: boolean;
  enableSmartRouting: boolean;
  enableDuplicateDetection: boolean;
  duplicateThreshold: number;
}

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: number;
  processedToday: number;
  errorRate: number;
  avgProcessingTime: number;
  queueSize: number;
  storageUsed: number;
  storageLimit: number;
}

export default function DocumentProcessingSettings() {
  const [settings, setSettings] = useState<ProcessingSettings | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState("general");
  const [testingConnection, setTestingConnection] = useState(false);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchSettings();
      fetchSystemHealth();
    }
  }, [selectedBusiness]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await AIConfigurationService.getProcessingSettings(selectedBusiness.id);
      if (response.data.result === "success") {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      // Mock data for development
      setSettings({
        enableAutoProcessing: true,
        maxConcurrentProcessing: 5,
        processingTimeout: 300,
        retryAttempts: 3,
        retryDelay: 5,
        confidenceThreshold: 0.8,
        manualReviewThreshold: 0.6,
        enableQualityChecks: true,
        storageProvider: 'aws_s3',
        retentionPeriod: 365,
        enableCompression: true,
        enableEncryption: true,
        enableEmailNotifications: true,
        enableSmsNotifications: false,
        enableWebhookNotifications: false,
        notificationEmail: 'admin@company.com',
        webhookUrl: '',
        enableAuditLogging: true,
        enableAccessControl: true,
        sessionTimeout: 30,
        enableTwoFactor: false,
        enableCaching: true,
        cacheExpiration: 3600,
        enableLoadBalancing: false,
        maxFileSize: 50,
        allowedFileTypes: ['pdf', 'jpg', 'jpeg', 'png', 'tiff'],
        enableWorkflowAutomation: true,
        enableSmartRouting: true,
        enableDuplicateDetection: true,
        duplicateThreshold: 0.9
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await AIConfigurationService.getSystemHealth();
      if (response.data.result === "success") {
        setSystemHealth(response.data.health);
      }
    } catch (error) {
      console.error("Failed to fetch system health:", error);
      // Mock data for development
      setSystemHealth({
        status: 'healthy',
        uptime: 99.9,
        processedToday: 1247,
        errorRate: 0.02,
        avgProcessingTime: 45,
        queueSize: 12,
        storageUsed: 2.4,
        storageLimit: 100
      });
    }
  };

  const handleSaveSettings = async () => {
    if (!settings || !selectedBusiness) return;

    try {
      setSaving(true);
      const response = await AIConfigurationService.updateProcessingSettings(selectedBusiness.id, settings);
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: "Settings saved successfully",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await AIConfigurationService.testConnection();
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: "Connection test successful",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Connection test failed",
        variant: "destructive",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const updateSetting = (key: keyof ProcessingSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="h-8 w-8" />
            Document Processing Settings
          </h1>
          <p className="text-muted-foreground">
            Configure AI processing, storage, security, and notification settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTestConnection} disabled={testingConnection}>
            {testingConnection ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Test Connection
          </Button>
          <Button onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {/* System Health Overview */}
      {systemHealth && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="text-center">
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${getHealthStatusColor(systemHealth.status)}`}>
                  {getHealthIcon(systemHealth.status)}
                  {systemHealth.status}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Status</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{systemHealth.uptime}%</p>
                <p className="text-xs text-muted-foreground">Uptime</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{systemHealth.processedToday}</p>
                <p className="text-xs text-muted-foreground">Processed Today</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{(systemHealth.errorRate * 100).toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">Error Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{systemHealth.avgProcessingTime}s</p>
                <p className="text-xs text-muted-foreground">Avg Processing</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{systemHealth.queueSize}</p>
                <p className="text-xs text-muted-foreground">Queue Size</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{systemHealth.storageUsed}GB</p>
                <p className="text-xs text-muted-foreground">Storage Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                General Processing Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="auto-processing">Enable Auto Processing</Label>
                    <Switch
                      id="auto-processing"
                      checked={settings.enableAutoProcessing}
                      onCheckedChange={(checked) => updateSetting('enableAutoProcessing', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Automatically process uploaded documents
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-concurrent">Max Concurrent Processing</Label>
                  <Input
                    id="max-concurrent"
                    type="number"
                    value={settings.maxConcurrentProcessing}
                    onChange={(e) => updateSetting('maxConcurrentProcessing', parseInt(e.target.value))}
                    min="1"
                    max="20"
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum number of documents to process simultaneously
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="processing-timeout">Processing Timeout (seconds)</Label>
                  <Input
                    id="processing-timeout"
                    type="number"
                    value={settings.processingTimeout}
                    onChange={(e) => updateSetting('processingTimeout', parseInt(e.target.value))}
                    min="30"
                    max="3600"
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum time to wait for processing completion
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="retry-attempts">Retry Attempts</Label>
                  <Input
                    id="retry-attempts"
                    type="number"
                    value={settings.retryAttempts}
                    onChange={(e) => updateSetting('retryAttempts', parseInt(e.target.value))}
                    min="0"
                    max="10"
                  />
                  <p className="text-sm text-muted-foreground">
                    Number of retry attempts for failed processing
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quality Settings */}
        <TabsContent value="quality" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Quality Control Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="confidence-threshold">Confidence Threshold</Label>
                  <Input
                    id="confidence-threshold"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={settings.confidenceThreshold}
                    onChange={(e) => updateSetting('confidenceThreshold', parseFloat(e.target.value))}
                  />
                  <p className="text-sm text-muted-foreground">
                    Minimum confidence score for auto-approval (0.0 - 1.0)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manual-review-threshold">Manual Review Threshold</Label>
                  <Input
                    id="manual-review-threshold"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={settings.manualReviewThreshold}
                    onChange={(e) => updateSetting('manualReviewThreshold', parseFloat(e.target.value))}
                  />
                  <p className="text-sm text-muted-foreground">
                    Confidence score below which manual review is required
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="quality-checks">Enable Quality Checks</Label>
                    <Switch
                      id="quality-checks"
                      checked={settings.enableQualityChecks}
                      onCheckedChange={(checked) => updateSetting('enableQualityChecks', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Perform additional quality validation on extracted data
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="duplicate-detection">Enable Duplicate Detection</Label>
                    <Switch
                      id="duplicate-detection"
                      checked={settings.enableDuplicateDetection}
                      onCheckedChange={(checked) => updateSetting('enableDuplicateDetection', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Detect and flag potential duplicate documents
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Settings */}
        <TabsContent value="storage" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Storage & Retention Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="storage-provider">Storage Provider</Label>
                  <Select
                    value={settings.storageProvider}
                    onValueChange={(value) => updateSetting('storageProvider', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">Local Storage</SelectItem>
                      <SelectItem value="aws_s3">AWS S3</SelectItem>
                      <SelectItem value="google_cloud">Google Cloud Storage</SelectItem>
                      <SelectItem value="azure_blob">Azure Blob Storage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="retention-period">Retention Period (days)</Label>
                  <Input
                    id="retention-period"
                    type="number"
                    value={settings.retentionPeriod}
                    onChange={(e) => updateSetting('retentionPeriod', parseInt(e.target.value))}
                    min="1"
                    max="3650"
                  />
                  <p className="text-sm text-muted-foreground">
                    How long to keep processed documents
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enable-compression">Enable Compression</Label>
                    <Switch
                      id="enable-compression"
                      checked={settings.enableCompression}
                      onCheckedChange={(checked) => updateSetting('enableCompression', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Compress files to save storage space
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enable-encryption">Enable Encryption</Label>
                    <Switch
                      id="enable-encryption"
                      checked={settings.enableEncryption}
                      onCheckedChange={(checked) => updateSetting('enableEncryption', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Encrypt files at rest for security
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="email-notifications">Email Notifications</Label>
                    <Switch
                      id="email-notifications"
                      checked={settings.enableEmailNotifications}
                      onCheckedChange={(checked) => updateSetting('enableEmailNotifications', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Send email notifications for processing events
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notification-email">Notification Email</Label>
                  <Input
                    id="notification-email"
                    type="email"
                    value={settings.notificationEmail}
                    onChange={(e) => updateSetting('notificationEmail', e.target.value)}
                    disabled={!settings.enableEmailNotifications}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="sms-notifications">SMS Notifications</Label>
                    <Switch
                      id="sms-notifications"
                      checked={settings.enableSmsNotifications}
                      onCheckedChange={(checked) => updateSetting('enableSmsNotifications', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Send SMS notifications for critical events
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="webhook-notifications">Webhook Notifications</Label>
                    <Switch
                      id="webhook-notifications"
                      checked={settings.enableWebhookNotifications}
                      onCheckedChange={(checked) => updateSetting('enableWebhookNotifications', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Send webhook notifications to external systems
                  </p>
                </div>

                {settings.enableWebhookNotifications && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="webhook-url">Webhook URL</Label>
                    <Input
                      id="webhook-url"
                      type="url"
                      value={settings.webhookUrl}
                      onChange={(e) => updateSetting('webhookUrl', e.target.value)}
                      placeholder="https://your-webhook-endpoint.com/webhook"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="audit-logging">Enable Audit Logging</Label>
                    <Switch
                      id="audit-logging"
                      checked={settings.enableAuditLogging}
                      onCheckedChange={(checked) => updateSetting('enableAuditLogging', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Log all user actions and system events
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="access-control">Enable Access Control</Label>
                    <Switch
                      id="access-control"
                      checked={settings.enableAccessControl}
                      onCheckedChange={(checked) => updateSetting('enableAccessControl', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enforce role-based access control
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
                  <Input
                    id="session-timeout"
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                    min="5"
                    max="480"
                  />
                  <p className="text-sm text-muted-foreground">
                    Automatic session timeout for security
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="two-factor">Enable Two-Factor Authentication</Label>
                    <Switch
                      id="two-factor"
                      checked={settings.enableTwoFactor}
                      onCheckedChange={(checked) => updateSetting('enableTwoFactor', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Require 2FA for sensitive operations
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Settings */}
        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Performance Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enable-caching">Enable Caching</Label>
                    <Switch
                      id="enable-caching"
                      checked={settings.enableCaching}
                      onCheckedChange={(checked) => updateSetting('enableCaching', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Cache processed results for faster retrieval
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cache-expiration">Cache Expiration (seconds)</Label>
                  <Input
                    id="cache-expiration"
                    type="number"
                    value={settings.cacheExpiration}
                    onChange={(e) => updateSetting('cacheExpiration', parseInt(e.target.value))}
                    min="300"
                    max="86400"
                    disabled={!settings.enableCaching}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-file-size">Max File Size (MB)</Label>
                  <Input
                    id="max-file-size"
                    type="number"
                    value={settings.maxFileSize}
                    onChange={(e) => updateSetting('maxFileSize', parseInt(e.target.value))}
                    min="1"
                    max="500"
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum allowed file size for uploads
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="load-balancing">Enable Load Balancing</Label>
                    <Switch
                      id="load-balancing"
                      checked={settings.enableLoadBalancing}
                      onCheckedChange={(checked) => updateSetting('enableLoadBalancing', checked)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Distribute processing load across multiple servers
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Allowed File Types</h4>
                <div className="flex flex-wrap gap-2">
                  {['pdf', 'jpg', 'jpeg', 'png', 'tiff', 'bmp', 'gif', 'webp'].map((type) => (
                    <Badge
                      key={type}
                      variant={settings.allowedFileTypes.includes(type) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        const newTypes = settings.allowedFileTypes.includes(type)
                          ? settings.allowedFileTypes.filter(t => t !== type)
                          : [...settings.allowedFileTypes, type];
                        updateSetting('allowedFileTypes', newTypes);
                      }}
                    >
                      {type.toUpperCase()}
                    </Badge>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Click to toggle allowed file types for upload
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 